import { QueryKey as TanStackQueryKey } from "@tanstack/react-query";
import { QueryKeyArgs, QueryKeyId } from "./key.type";

/**
 * Base class for query key operations that provides the common builder functionality.
 *
 * This class contains the core builder method that constructs query keys from the provided parameters.
 * The order of the items which compose the queryKey is important. These items should go from generic to specific.
 *
 * @template TAfterPage - The type returned after calling the page method
 * @template TAfterLocale - The type returned after calling the locale method
 * @template TAfterGroup - The type returned after calling the group method
 * @template TAfterScopes - The type returned after calling the scopes method
 * @template TAfterId - The type returned after calling the id method
 * @template TAfterArgs - The type returned after calling the args method
 * @abstract
 */
abstract class BaseQueryKey<
    TAfterPage = any,
    TAfterLocale = any,
    TAfterGroup = any,
    TAfterScopes = any,
    TAfterId = any,
    TAfterArgs = any
> {
    protected _page: string = "";
    protected _locale: string = "en";
    protected _group?: string;
    protected _scopes: (string | number | symbol | undefined | null)[] = [];
    protected _id?: QueryKeyId;
    protected _args: QueryKeyArgs[] = [];

    /**
     * Factory methods to create the return objects for each step in the fluent interface.
     * These methods should be implemented by subclasses to provide the appropriate return types.
     *
     * @abstract
     */
    protected abstract createAfterPageInterface(): TAfterPage;
    protected abstract createAfterLocaleInterface(): TAfterLocale;
    protected abstract createAfterGroupInterface(): TAfterGroup;
    protected abstract createAfterScopesInterface(): TAfterScopes;
    protected abstract createAfterIdInterface(): TAfterId;
    protected abstract createAfterArgsInterface(): TAfterArgs;

    /**
     * Set the page for the query key.
     *
     * @param {string} page - The current page path.
     * @returns {TAfterPage} - Returns interface for next available methods.
     */
    public page(page: string): TAfterPage {
        this._page = page;
        return this.createAfterPageInterface();
    }

    /**
     * Set the locale for the query key.
     *
     * @param {string} locale - The current locale/language.
     * @returns {TAfterLocale} - Returns interface for next available methods.
     */
    public locale(locale: string): TAfterLocale {
        this._locale = locale;
        return this.createAfterLocaleInterface();
    }

    /**
     * Set the group for the query key.
     *
     * @param {string} group - A string representing the group or category of the query.
     * @returns {TAfterGroup} - Returns interface for next available methods.
     */
    public group(group: string): TAfterGroup {
        this._group = group;
        return this.createAfterGroupInterface();
    }

    /**
     * Set the scopes for the query key.
     *
     * @param {(string | number | symbol | undefined | null)[]} scopes - An array representing different scopes or contexts for the query.
     * @returns {TAfterScopes} - Returns interface for next available methods.
     */
    public scopes(scopes: (string | number | symbol | undefined | null)[]): TAfterScopes {
        this._scopes = scopes;
        return this.createAfterScopesInterface();
    }

    /**
     * Set the id for the query key.
     *
     * @param {QueryKeyId} id - An optional identifier for the query.
     * @returns {TAfterId} - Returns interface for next available methods.
     */
    public id(id: QueryKeyId): TAfterId {
        this._id = id;
        return this.createAfterIdInterface();
    }

    /**
     * Add additional arguments to the query key.
     *
     * @param {...QueryKeyArgs[]} args - Additional arguments to be included in the query key.
     * @returns {TAfterArgs} - Returns interface for next available methods.
     */
    public args(...args: QueryKeyArgs[]): TAfterArgs {
        this._args.push(...args);
        return this.createAfterArgsInterface();
    }

    /**
     * Build a query key from the protected properties.
     *
     * @returns {TanStackQueryKey} - An array representing the generated query key.
     */
    protected builder(): TanStackQueryKey {
        const page = { page: this._args.find((arg) => arg.page)?.page || this._page };
        const language = { language: this._args.find((arg) => arg.page)?.language || this._locale };
        const optional = this._args.filter((arg) => !arg.page);

        const queryKey: unknown[] = [page, language];

        if (this._group) {
            queryKey.push({ group: this._group });
        }

        if (this._scopes.filter(Boolean).length > 0) {
            queryKey.push({ scopes: this._scopes });
        }

        if (this._id) {
            queryKey.push({ id: this._id });
        }

        if (optional.length > 0) {
            queryKey.push({ optional });
        }

        return queryKey;
    }

    /**
     * Reset all properties to their default values.
     * Useful for reusing the same instance across multiple query key generations.
     */
    protected reset(): void {
        this._page = "";
        this._locale = "en";
        this._group = undefined;
        this._scopes = [];
        this._id = undefined;
        this._args = [];
    }
}

export { BaseQueryKey };
export default BaseQueryKey;
